import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import PlanGuard from '@/components/PlanGuard';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { motion } from 'framer-motion';
import axios from 'axios';
import { FaArrowLeft, FaDumbbell, FaSpinner, FaEnvelope, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import { toast } from 'sonner';
import ContinuationForm from '@/components/ContinuationForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WorkoutGenerator = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isContinuation = searchParams.get('continuation') === 'true';
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [catalogExercises, setCatalogExercises] = useState([]); // {id, title, muscle_group}
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [exerciseSearch, setExerciseSearch] = useState('');
  // 2026-02 · Template dinámico desde `form_templates` (form_type=fit_evolution_initial).
  // El formulario sigue renderizando la misma UI, pero los labels/descripciones/
  // opciones de los campos estándar vienen del template (editable por el admin).
  const [formTemplate, setFormTemplate] = useState(null);
  const [formData, setFormData] = useState({
    level: '',
    trainingDays: '',
    sessionDuration: '',
    problematicExercises: '',
    excludedExerciseIds: [],
    cardioTimes: [],
    goal: '',
    trainingLocation: '',
    muscleGroups: []
  });

  // 2026-02 · Cargar template editable desde el backend en el montaje.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('deisycfit_token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await axios.get(
          `${API}/form-templates/active?form_type=fit_evolution_initial&plan=basic`,
          { headers, withCredentials: false }
        );
        if (!cancelled && res.data?.template) setFormTemplate(res.data.template);
      } catch (e) {
        // silencio: si no hay template usamos los defaults hardcodeados
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Helpers para leer del template con fallback a valores hardcodeados.
  const tplField = (fieldId) => {
    return (formTemplate?.fields || []).find((f) => f.id === fieldId) || null;
  };
  const tplLabel = (fieldId, fallback) => tplField(fieldId)?.label || fallback;
  const tplDesc = (fieldId, fallback = '') => tplField(fieldId)?.description || fallback;
  const tplOptions = (fieldId, fallback) => {
    const f = tplField(fieldId);
    return Array.isArray(f?.options) && f.options.length > 0 ? f.options : fallback;
  };

  // Cargar catálogo de ejercicios filtrado por género de la usuaria
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setCatalogLoading(true);
      try {
        const token = localStorage.getItem('deisycfit_token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const prof = await axios.get(`${API}/user/profile`, { headers, withCredentials: false }).catch(() => null);
        const rawGender = prof?.data?.gender || prof?.data?.user?.gender || '';
        const g = String(rawGender || '').toLowerCase();
        let param = '';
        if (['female','femenino','mujer','f'].includes(g)) param = '?gender=femenino';
        else if (['male','masculino','hombre','m'].includes(g)) param = '?gender=masculino';
        const res = await axios.get(`${API}/videos/for-user-form${param}`);
        if (!cancelled) setCatalogExercises(res.data?.exercises || []);
      } catch (e) {
        if (!cancelled) setCatalogExercises([]);
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Si es formulario de continuación, mostrar ContinuationForm
  if (isContinuation) {
    return (
      <PlanGuard allowedPlans={['basic']}>
        <div className="min-h-screen py-12 px-4" style={{ background: 'linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%)' }}>
          <div className="max-w-4xl mx-auto">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-gray-100 hover:text-white mb-8 transition-colors"
            >
              <FaArrowLeft /> Volver al Dashboard
            </button>
            <ContinuationForm 
              formType="workout" 
              onSuccess={() => navigate('/dashboard')}
              onCancel={() => navigate('/dashboard')}
            />
          </div>
        </div>
      </PlanGuard>
    );
  }

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleMuscleGroupToggle = (muscle) => {
    setFormData(prev => ({
      ...prev,
      muscleGroups: prev.muscleGroups.includes(muscle)
        ? prev.muscleGroups.filter(m => m !== muscle)
        : [...prev.muscleGroups, muscle]
    }));
  };

  const nextStep = () => {
    if (step === 1 && (!formData.level || !formData.goal)) {
      toast.error('Completa tu nivel de experiencia y objetivo');
      return;
    }
    if (step === 2 && !formData.trainingDays) {
      toast.error('Selecciona cuántos días vas a entrenar');
      return;
    }
    if (step === 3 && !formData.sessionDuration) {
      toast.error('Selecciona la duración de tus sesiones');
      return;
    }
    if (step === 3 && !formData.trainingLocation) {
      toast.error('Selecciona dónde vas a entrenar');
      return;
    }
    setStep(step + 1);
  };

  const prevStep = () => setStep(step - 1);

  const handleGenerate = async () => {
    if (formData.cardioTimes.length === 0) {
      toast.error('Selecciona al menos un momento para hacer cardio (o selecciona "No haré cardio")');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('deisycfit_token');
      const response = await axios.post(`${API}/workout/generate`, formData, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });

      if (response.data.pending_release) {
        setShowConfirmation(true);
      } else {
        toast.success('¡Rutina generada exitosamente!');
        navigate('/my-workout');
      }
    } catch (error) {
      console.error('Error generating workout:', error);
      toast.error('Error al generar la rutina. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  // Pantalla de confirmación con mensaje de spam
  if (showConfirmation) {
    return (
      <div className="min-h-screen py-12 px-6" style={{ backgroundColor: '#0a0a0a' }}>
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="rounded-2xl shadow-xl p-8 text-center"
            style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
          >
            {/* Icono de éxito */}
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-6" style={{ backgroundColor: 'rgba(34, 197, 94, 0.2)' }}>
              <FaCheckCircle className="text-4xl text-green-400" />
            </div>
            
            <h1 className="text-3xl font-bold text-white mb-4">
              ¡Formulario Enviado Exitosamente! 🎉
            </h1>
            
            <p className="text-lg text-white text-base font-semibold mb-6">
              Estamos trabajando en tu rutina de entrenamiento personalizada. 
              <strong className="text-[var(--brand-primary)]"> En un plazo de 24 horas</strong> recibirás una notificación 
              cuando esté lista.
            </p>

            {/* Alerta de SPAM - Importante */}
            <div className="rounded-xl p-6 mb-6 text-left" style={{ backgroundColor: 'rgba(245, 158, 11, 0.15)', border: '2px solid rgba(245, 158, 11, 0.3)' }}>
              <div className="flex items-start gap-3">
                <FaExclamationTriangle className="text-amber-400 text-2xl flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-amber-400 text-lg mb-2">
                    ⚠️ ¡Importante! Revisa tu bandeja de Spam
                  </h3>
                  <p className="text-amber-300 mb-3">
                    Para que no te pierdas nuestras actualizaciones y te lleguen las notificaciones 
                    de tu proceso, asegúrate de lo siguiente:
                  </p>
                  <ul className="text-amber-300 space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-amber-400">•</span>
                      <span>Revisa tu carpeta de <strong>Spam o Correo no deseado</strong></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-amber-400">•</span>
                      <span>Si encuentras nuestro email ahí, márcalo como <strong>&quot;No es spam&quot;</strong></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-amber-400">•</span>
                      <span>Agrega <strong>deisycpbusiness@gmail.com</strong> a tus contactos</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Nota de tiempo de espera */}
            <div className="rounded-xl p-4 mb-6 text-left" style={{ backgroundColor: 'rgba(0, 240, 255, 0.1)', border: '1px solid rgba(0, 240, 255, 0.3)' }}>
              <p className="text-[#00F0FF] text-sm">
                <strong>📝 Nota:</strong> Si en <strong>24 horas</strong> no recibes tu plan, por favor escríbenos al 
                WhatsApp que aparece en la página indicando tu <strong>correo electrónico</strong> para revisar tu solicitud en el sistema.
              </p>
            </div>

            {/* Info del email */}
            <div className="rounded-xl p-4 mb-6 flex items-center justify-center gap-3" style={{ backgroundColor: 'rgba(var(--brand-primary-glow), 0.15)' }}>
              <FaEnvelope className="text-[var(--brand-primary)] text-xl" />
              <span className="text-white text-base font-semibold">
                Te hemos enviado un email de confirmación a tu correo registrado
              </span>
            </div>

            {/* Botones de acción */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => navigate('/dashboard')}
                className="text-white font-bold rounded-full"
                style={{ background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}
              >
                Ir al Dashboard
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <PlanGuard requiredPlan="basic" featureName="Tu Rutina Personalizada">
    <div className="min-h-screen py-12 px-6" style={{ backgroundColor: '#0a0a0a' }}>
      <div className="max-w-4xl mx-auto">
        <Button variant="outline" onClick={() => navigate('/dashboard')} className="mb-6 border-[var(--brand-primary)] text-[var(--brand-primary)] hover:bg-[var(--brand-primary)]/10">
          <FaArrowLeft className="mr-2" /> Volver
        </Button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full text-white mb-4" style={{ background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}>
            <FaDumbbell className="text-4xl" />
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold mb-4 text-white">💪 Mi Rutina de Entrenamiento</h1>
          <p className="text-lg text-gray-100">Cuéntanos sobre ti y creamos tu plan perfecto</p>
          
          {/* Progress Indicator */}
          <div className="flex justify-center gap-2 mt-6">
            {[1, 2, 3, 4].map((s) => (
              <div
                key={s}
                className="h-2 w-16 rounded-full transition-all"
                style={{ backgroundColor: s <= step ? 'var(--brand-primary)' : 'rgba(255,255,255,0.2)' }}
              />
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <CardHeader>
              <CardTitle className="text-white">Paso {step} de 4</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              
              {/* Step 1: Nivel y Objetivo */}
              {step === 1 && (
                <div className="space-y-6">
                  <div className="space-y-4">
                    <h3 className="text-xl font-semibold text-white">
                      {tplLabel('level', '¿Cuál es tu nivel de experiencia?')}
                    </h3>
                    <div className="grid gap-4">
                      {tplOptions('level', [
                        { value: 'novato', label: 'Novato', description: 'Menos de 6 meses de experiencia' },
                        { value: 'intermedio', label: 'Intermedio', description: '6 meses - 2 años de experiencia' },
                        { value: 'avanzado', label: 'Avanzado', description: 'Más de 2 años de experiencia' }
                      ]).map((level) => (
                        <div
                          key={level.value}
                          className="p-4 border-2 rounded-xl cursor-pointer transition-all"
                          style={{ 
                            borderColor: formData.level === level.value ? 'var(--brand-primary)' : 'rgba(255,255,255,0.1)',
                            backgroundColor: formData.level === level.value ? 'rgba(var(--brand-primary-glow), 0.15)' : 'rgba(255,255,255,0.05)'
                          }}
                          onClick={() => handleChange('level', level.value)}
                        >
                          <div className="font-semibold text-lg text-white">{level.label}</div>
                          <div className="text-sm text-gray-100">{level.description || level.desc}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-xl font-semibold text-white">
                      {tplLabel('goal', '¿Cuál es tu objetivo principal?')}
                    </h3>
                    <div className="grid gap-4">
                      {tplOptions('goal', [
                        { value: 'perder_grasa', label: 'Perder Grasa', description: 'Reducir porcentaje de grasa corporal' },
                        { value: 'ganar_musculo', label: 'Ganar Músculo', description: 'Aumentar masa muscular' },
                        { value: 'mantener', label: 'Mantener', description: 'Mantener mi físico actual' },
                        { value: 'recomposicion', label: 'Recomposición Corporal', description: 'Aumentar masa muscular y bajar grasa al mismo tiempo' }
                      ]).map((goal) => (
                        <div
                          key={goal.value}
                          className="p-4 border-2 rounded-xl cursor-pointer transition-all"
                          style={{ 
                            borderColor: formData.goal === goal.value ? 'var(--brand-primary)' : 'rgba(255,255,255,0.1)',
                            backgroundColor: formData.goal === goal.value ? 'rgba(var(--brand-primary-glow), 0.15)' : 'rgba(255,255,255,0.05)'
                          }}
                          onClick={() => handleChange('goal', goal.value)}
                        >
                          <div className="font-semibold text-lg text-white">{goal.label}</div>
                          <div className="text-sm text-gray-100">{goal.description || goal.desc}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Días de entrenamiento */}
              {step === 2 && (
                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-white">
                    {tplLabel('trainingDays', '¿Cuántos días por semana vas a entrenar?')}
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    {tplOptions('trainingDays', [
                      { value: '3', label: '3 días' },
                      { value: '4', label: '4 días' },
                      { value: '5', label: '5 días' },
                      { value: '6', label: '6 días' },
                    ]).map((opt) => {
                      const days = String(opt.value);
                      return (
                        <div
                          key={days}
                          className="p-6 border-2 rounded-xl cursor-pointer transition-all text-center"
                          style={{ 
                            borderColor: formData.trainingDays === days ? 'var(--brand-primary)' : 'rgba(255,255,255,0.1)',
                            backgroundColor: formData.trainingDays === days ? 'rgba(var(--brand-primary-glow), 0.15)' : 'rgba(255,255,255,0.05)'
                          }}
                          onClick={() => handleChange('trainingDays', days)}
                        >
                          <div className="text-3xl font-bold text-[var(--brand-primary)]">{days}</div>
                          <div className="text-sm text-gray-100">días</div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Muscle Groups Priority */}
                  <div className="mt-6">
                    <h4 className="font-semibold mb-3 text-white">
                      {tplLabel('muscleGroups', '¿Qué grupos musculares quieres priorizar?')}
                    </h4>
                    <p className="text-sm text-gray-100 mb-3">
                      {tplDesc('muscleGroups', 'Selecciona uno o más (opcional)')}
                    </p>
                    {(() => {
                      const opts = tplOptions('muscleGroups', []);
                      // Si el template define options con `group`, agrupamos.
                      if (opts.length > 0 && opts.some((o) => o.group)) {
                        const groups = {};
                        opts.forEach((o) => {
                          const g = o.group || 'General';
                          (groups[g] = groups[g] || []).push(o);
                        });
                        const groupColor = (g) => {
                          if (g.includes('Tren Inferior')) return { active: 'bg-[var(--brand-primary)]', title: 'text-[var(--brand-primary)]' };
                          if (g.includes('Tren Superior')) return { active: 'bg-[#00F0FF] text-black', title: 'text-[#00F0FF]' };
                          if (g.includes('Core')) return { active: 'bg-amber-500', title: 'text-amber-400' };
                          return { active: 'bg-purple-500', title: 'text-purple-400' };
                        };
                        return Object.entries(groups).map(([g, list]) => {
                          const c = groupColor(g);
                          return (
                            <div key={g} className="mb-4">
                              <h5 className={`text-sm font-semibold ${c.title} mb-2`}>{g.toUpperCase()}</h5>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                {list.map((m) => (
                                  <Badge
                                    key={m.value}
                                    variant={formData.muscleGroups.includes(m.value) ? 'default' : 'outline'}
                                    className={`cursor-pointer py-2 text-center justify-center text-xs ${
                                      formData.muscleGroups.includes(m.value) ? c.active : 'border-white/20 text-white text-base font-semibold'
                                    }`}
                                    onClick={() => handleMuscleGroupToggle(m.value)}
                                  >
                                    {m.label}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          );
                        });
                      }
                      // Fallback hardcodeado (misma UI que antes).
                      return (
                        <>
                          <div className="mb-4">
                            <h5 className="text-sm font-semibold text-[var(--brand-primary)] mb-2">🦵 TREN INFERIOR</h5>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                              {['Cuádriceps', 'Isquiotibiales', 'Glúteo Mayor', 'Glúteo Medio', 'Glúteo Menor', 'Aductores', 'Abductores', 'Pantorrillas', 'Lumbares'].map((muscle) => (
                                <Badge
                                  key={muscle}
                                  variant={formData.muscleGroups.includes(muscle) ? 'default' : 'outline'}
                                  className={`cursor-pointer py-2 text-center justify-center text-xs ${
                                    formData.muscleGroups.includes(muscle) ? 'bg-[var(--brand-primary)]' : 'border-white/20 text-white text-base font-semibold'
                                  }`}
                                  onClick={() => handleMuscleGroupToggle(muscle)}
                                >
                                  {muscle}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          <div className="mb-4">
                            <h5 className="text-sm font-semibold text-[#00F0FF] mb-2">💪 TREN SUPERIOR</h5>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                              {['Pecho', 'Espalda Alta', 'Espalda Media', 'Dorsales', 'Hombro Anterior', 'Hombro Medio', 'Hombro Posterior', 'Trapecio', 'Bíceps', 'Tríceps', 'Antebrazos'].map((muscle) => (
                                <Badge
                                  key={muscle}
                                  variant={formData.muscleGroups.includes(muscle) ? 'default' : 'outline'}
                                  className={`cursor-pointer py-2 text-center justify-center text-xs ${
                                    formData.muscleGroups.includes(muscle) ? 'bg-[#00F0FF] text-black' : 'border-white/20 text-white text-base font-semibold'
                                  }`}
                                  onClick={() => handleMuscleGroupToggle(muscle)}
                                >
                                  {muscle}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          <div>
                            <h5 className="text-sm font-semibold text-amber-400 mb-2">🔥 CORE</h5>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                              {['Abdomen Superior', 'Abdomen Inferior', 'Oblicuos', 'Core/Transverso'].map((muscle) => (
                                <Badge
                                  key={muscle}
                                  variant={formData.muscleGroups.includes(muscle) ? 'default' : 'outline'}
                                  className={`cursor-pointer py-2 text-center justify-center text-xs ${
                                    formData.muscleGroups.includes(muscle) ? 'bg-amber-500' : 'border-white/20 text-white text-base font-semibold'
                                  }`}
                                  onClick={() => handleMuscleGroupToggle(muscle)}
                                >
                                  {muscle}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* Step 3: Duración, Lugar y Ejercicios problemáticos */}
              {step === 3 && (
                <div className="space-y-6">
                  <div className="space-y-4">
                    <h3 className="text-xl font-semibold text-white">
                      {tplLabel('sessionDuration', '¿Cuánto tiempo tienes para entrenar?')}
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      {tplOptions('sessionDuration', [
                        { value: '45min', label: '45 minutos' },
                        { value: '60min', label: '60 minutos' },
                        { value: '75min', label: '75 minutos' },
                        { value: '90min', label: '90+ minutos' }
                      ]).map((duration) => (
                        <div
                          key={duration.value}
                          className="p-4 border-2 rounded-xl cursor-pointer transition-all text-center"
                          style={{ 
                            borderColor: formData.sessionDuration === duration.value ? 'var(--brand-primary)' : 'rgba(255,255,255,0.1)',
                            backgroundColor: formData.sessionDuration === duration.value ? 'rgba(var(--brand-primary-glow), 0.15)' : 'rgba(255,255,255,0.05)'
                          }}
                          onClick={() => handleChange('sessionDuration', duration.value)}
                        >
                          <div className="font-semibold text-white">{duration.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-xl font-semibold text-white">
                      {tplLabel('trainingLocation', '¿Dónde vas a entrenar?')}
                    </h3>
                    <p className="text-sm text-gray-100">
                      {tplDesc('trainingLocation', 'Elige el lugar donde harás tus rutinas la mayor parte del tiempo')}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {tplOptions('trainingLocation', [
                        { value: 'casa', label: 'En Casa', description: 'Con material básico o sin equipamiento', icon: '🏠' },
                        { value: 'gym', label: 'En el Gym', description: 'Acceso a máquinas y peso libre', icon: '🏋️' },
                        { value: 'ambos', label: 'Ambos', description: 'A veces casa, a veces gym', icon: '🔀' }
                      ]).map((loc) => (
                        <div
                          key={loc.value}
                          data-testid={`training-location-${loc.value}`}
                          className="p-4 border-2 rounded-xl cursor-pointer transition-all text-center"
                          style={{
                            borderColor: formData.trainingLocation === loc.value ? 'var(--brand-primary)' : 'rgba(255,255,255,0.1)',
                            backgroundColor: formData.trainingLocation === loc.value ? 'rgba(var(--brand-primary-glow), 0.15)' : 'rgba(255,255,255,0.05)'
                          }}
                          onClick={() => handleChange('trainingLocation', loc.value)}
                        >
                          <div className="text-3xl mb-1">{loc.icon}</div>
                          <div className="font-semibold text-white text-base">{loc.label}</div>
                          <div className="text-xs text-gray-100 mt-1">{loc.description || loc.desc}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <h3 className="text-xl font-semibold text-white">
                      {tplLabel('excludedExerciseIds', '¿Algún ejercicio te genera molestia?')}
                    </h3>
                    <p className="text-sm text-gray-100">
                      {tplDesc('excludedExerciseIds', 'Marca los que quieres evitar. Puedes seleccionar varios o dejar la lista vacía.')}
                    </p>

                    {/* Selector múltiple del catálogo */}
                    <div
                      className="rounded-lg border-2 p-3"
                      style={{ backgroundColor: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.2)' }}
                      data-testid="excluded-exercises-selector"
                    >
                      <input
                        type="text"
                        placeholder="Buscar en el catálogo…"
                        className="w-full mb-2 px-3 py-2 rounded-md text-white text-sm placeholder:text-gray-300 focus:outline-none"
                        style={{ backgroundColor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)' }}
                        value={exerciseSearch}
                        onChange={(e) => setExerciseSearch(e.target.value)}
                        data-testid="excluded-exercises-search"
                      />
                      <div
                        className="max-h-64 overflow-y-auto pr-1 space-y-1"
                        style={{ scrollbarWidth: 'thin' }}
                      >
                        {catalogLoading && (
                          <p className="text-xs text-gray-200 py-2 text-center">Cargando catálogo…</p>
                        )}
                        {!catalogLoading && catalogExercises.length === 0 && (
                          <p className="text-xs text-gray-200 py-2 text-center">
                            No hay ejercicios en el catálogo por ahora.
                          </p>
                        )}
                        {!catalogLoading && (() => {
                          const q = exerciseSearch.trim().toLowerCase();
                          const list = q
                            ? catalogExercises.filter(ex =>
                                (ex.title || '').toLowerCase().includes(q) ||
                                (ex.muscle_group || '').toLowerCase().includes(q))
                            : catalogExercises;
                          // Agrupar por muscle_group
                          const groups = {};
                          list.forEach(ex => {
                            const k = ex.muscle_group || 'Otros';
                            (groups[k] = groups[k] || []).push(ex);
                          });
                          return Object.entries(groups).map(([mg, exs]) => (
                            <div key={mg} className="mb-1">
                              <div className="text-[10px] uppercase tracking-wider text-gray-300 mt-2 mb-1">{mg}</div>
                              {exs.map(ex => {
                                const checked = formData.excludedExerciseIds.includes(ex.id);
                                return (
                                  <label
                                    key={ex.id}
                                    className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-white/5 cursor-pointer"
                                    data-testid={`excluded-exercise-option-${ex.id}`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={() => {
                                        const next = checked
                                          ? formData.excludedExerciseIds.filter(x => x !== ex.id)
                                          : [...formData.excludedExerciseIds, ex.id];
                                        handleChange('excludedExerciseIds', next);
                                      }}
                                      className="accent-pink-500"
                                    />
                                    <span className="text-sm text-white">{ex.title}</span>
                                  </label>
                                );
                              })}
                            </div>
                          ));
                        })()}
                      </div>
                      {formData.excludedExerciseIds.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-white/10 flex items-center justify-between gap-2">
                          <span className="text-xs text-gray-100">
                            {formData.excludedExerciseIds.length} ejercicio(s) seleccionado(s)
                          </span>
                          <button
                            type="button"
                            onClick={() => handleChange('excludedExerciseIds', [])}
                            className="text-xs text-pink-300 hover:text-pink-200 underline"
                            data-testid="excluded-exercises-clear"
                          >
                            Limpiar
                          </button>
                        </div>
                      )}
                    </div>

                    <p className="text-xs text-gray-200 italic">
                      Si no marcas ninguno, damos por entendido que puedes realizar todos los ejercicios. Deja esta casilla libre si aún no has empezado a entrenar.
                    </p>
                  </div>
                </div>
              )}

              {/* Step 4: Cardio */}
              {step === 4 && (
                <div className="space-y-6">
                  {/* Cardio Recommendation */}
                  <div className="p-4 rounded-lg" style={{ background: 'linear-gradient(135deg, rgba(var(--brand-primary-glow), 0.2), rgba(0, 240, 255, 0.2))', border: '2px solid rgba(var(--brand-primary-glow), 0.3)' }}>
                    <h4 className="font-semibold text-lg mb-2 text-white">📊 Recomendación de Cardio</h4>
                    <p className="text-sm text-white text-base font-semibold">
                      Según tu objetivo de <strong className="capitalize text-white">{formData.goal === 'perder_grasa' ? 'perder grasa' : formData.goal === 'ganar_musculo' ? 'ganar músculo' : formData.goal === 'recomposicion' ? 'recomposición corporal' : 'mantenimiento'}</strong>, 
                      debes hacer:
                    </p>
                    <p className="text-2xl font-bold text-[var(--brand-primary)] mt-2">
                      {formData.goal === 'perder_grasa' && '40-50 minutos de cardio al día'}
                      {formData.goal === 'ganar_musculo' && '20-30 minutos de cardio al día'}
                      {formData.goal === 'mantener' && '30-40 minutos de cardio al día'}
                      {formData.goal === 'recomposicion' && '30-40 minutos de cardio al día'}
                    </p>
                    <p className="text-xs text-gray-100 mt-2">
                      {formData.goal === 'perder_grasa' && 'Cardio intenso para maximizar quema de grasa'}
                      {formData.goal === 'ganar_musculo' && 'Cardio moderado para mantener salud cardiovascular sin afectar ganancia muscular'}
                      {formData.goal === 'mantener' && 'Cardio moderado para mantener condición física'}
                      {formData.goal === 'recomposicion' && 'Cardio moderado — se prioriza fuerza para mantener músculo mientras bajas grasa'}
                    </p>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-xl font-semibold text-white">
                      {tplLabel('cardioTimes', '¿En qué momentos del día harás cardio?')}
                    </h3>
                    <p className="text-sm text-gray-100">
                      {tplDesc('cardioTimes', 'Puedes seleccionar hasta 2 momentos (para dividir el cardio o hacer sesiones separadas)')}
                    </p>
                    <div className="grid gap-3">
                      {tplOptions('cardioTimes', [
                        { value: 'ayunas', label: 'En Ayunas (Mañana)', description: 'Antes del desayuno - Máxima quema de grasa', icon: '🌅' },
                        { value: 'post-entreno', label: 'Post Entrenamiento', description: 'Después de tu sesión de pesas', icon: '💪' },
                        { value: 'tarde', label: 'Tarde', description: 'Entre comidas por la tarde', icon: '☀️' },
                        { value: 'noche', label: 'Noche (antes de dormir)', description: 'Por la noche antes de acostarte', icon: '🌙' },
                        { value: 'no-cardio', label: 'No haré cardio', description: 'Solo entrenamiento de fuerza', icon: '❌' }
                      ]).map((cardioTime) => (
                        <div
                          key={cardioTime.value}
                          className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                            cardioTime.value === 'no-cardio' && formData.cardioTimes.length > 0 && formData.cardioTimes[0] !== 'no-cardio'
                              ? 'opacity-50 cursor-not-allowed'
                              : ''
                          }`}
                          style={{ 
                            borderColor: formData.cardioTimes.includes(cardioTime.value) ? 'var(--brand-primary)' : 'rgba(255,255,255,0.1)',
                            backgroundColor: formData.cardioTimes.includes(cardioTime.value) ? 'rgba(var(--brand-primary-glow), 0.15)' : 'rgba(255,255,255,0.05)'
                          }}
                          onClick={() => {
                            if (cardioTime.value === 'no-cardio') {
                              handleChange('cardioTimes', ['no-cardio']);
                            } else {
                              const newTimes = formData.cardioTimes.filter(t => t !== 'no-cardio');
                              if (newTimes.includes(cardioTime.value)) {
                                handleChange('cardioTimes', newTimes.filter(t => t !== cardioTime.value));
                              } else if (newTimes.length < 2) {
                                handleChange('cardioTimes', [...newTimes, cardioTime.value]);
                              } else {
                                toast.warning('Máximo 2 momentos para cardio');
                              }
                            }
                          }}
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">{cardioTime.icon}</span>
                            <div className="flex-1">
                              <div className="font-semibold text-lg text-white">{cardioTime.label}</div>
                              <div className="text-sm text-gray-100">{cardioTime.description || cardioTime.desc}</div>
                            </div>
                            {formData.cardioTimes.includes(cardioTime.value) && (
                              <span className="text-[var(--brand-primary)] font-bold">✓</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Summary */}
                  <div className="mt-8 p-6 rounded-xl" style={{ background: 'linear-gradient(135deg, rgba(var(--brand-primary-glow), 0.1), rgba(0, 240, 255, 0.1))', border: '2px solid rgba(var(--brand-primary-glow), 0.2)' }}>
                    <h4 className="font-semibold text-lg mb-4 text-white">Resumen de tu rutina:</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-100">Nivel:</span>
                        <span className="font-semibold capitalize text-white">{formData.level}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-100">Objetivo:</span>
                        <span className="font-semibold capitalize text-white">
                          {formData.goal === 'perder_grasa' ? 'Perder Grasa' : 
                           formData.goal === 'ganar_musculo' ? 'Ganar Músculo' :
                           formData.goal === 'recomposicion' ? 'Recomposición Corporal' : 'Mantener'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-100">Días por semana:</span>
                        <span className="font-semibold text-white">{formData.trainingDays} días</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-100">Duración:</span>
                        <span className="font-semibold text-white">{formData.sessionDuration}</span>
                      </div>
                      {formData.trainingLocation && (
                        <div className="flex justify-between">
                          <span className="text-gray-100">Lugar:</span>
                          <span className="font-semibold text-white capitalize">
                            {formData.trainingLocation === 'casa' ? '🏠 Casa' :
                             formData.trainingLocation === 'gym' ? '🏋️ Gym' : '🔀 Ambos'}
                          </span>
                        </div>
                      )}
                      {formData.cardioTimes.length > 0 && !formData.cardioTimes.includes('no-cardio') && (
                        <div className="flex justify-between">
                          <span className="text-gray-100">Cardio:</span>
                          <span className="font-semibold text-white">
                            {formData.cardioTimes.map(time => {
                              const labels = {
                                'ayunas': 'Ayunas',
                                'post-entreno': 'Post-entreno',
                                'tarde': 'Tarde',
                                'noche': 'Noche'
                              };
                              return labels[time];
                            }).join(' + ')}
                          </span>
                        </div>
                      )}
                      {formData.muscleGroups.length > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-100">Prioridad:</span>
                          <span className="font-semibold text-white">{formData.muscleGroups.join(', ')}</span>
                        </div>
                      )}
                      {formData.excludedExerciseIds.length > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-100">Evitar:</span>
                          <span className="font-semibold text-white text-right">
                            {catalogExercises
                              .filter(e => formData.excludedExerciseIds.includes(e.id))
                              .map(e => e.title)
                              .join(', ') || `${formData.excludedExerciseIds.length} ejercicio(s)`}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex gap-4 pt-6 border-t" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
                {step > 1 && (
                  <Button variant="outline" onClick={prevStep} className="flex-1 border-[var(--brand-primary)] text-[var(--brand-primary)] hover:bg-[var(--brand-primary)]/10 rounded-full">
                    Anterior
                  </Button>
                )}
                {step < 4 ? (
                  <Button
                    onClick={nextStep}
                    className="flex-1 text-white font-bold rounded-full"
                    style={{ background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}
                  >
                    Siguiente
                  </Button>
                ) : (
                  <Button
                    onClick={handleGenerate}
                    disabled={loading}
                    className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:opacity-90 text-white font-bold rounded-full"
                  >
                    {loading ? (
                      <>
                        <FaSpinner className="mr-2 animate-spin" /> Generando...
                      </>
                    ) : (
                      'Generar Mi Rutina'
                    )}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
    </PlanGuard>
  );
};

export default WorkoutGenerator;
